import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

const linkStyle = {
  color: '#9ca3af',
  textDecoration: 'none',
  fontSize: '14px',
  transition: 'color 0.2s ease',
  cursor: 'pointer'
};

/* UrCommunity homepage Smart Slider: hashToIndex '#chatbotfactory' -> slider.slide(5) (Chatbot Factory) */
const URCOMMUNITY_CHATBOT_FACTORY_URL = 'https://urcommunity.org/#chatbotfactory';

const Footer = () => {
  const [showDocModal, setShowDocModal] = useState(false);
  const [docList, setDocList] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [errorDoc, setErrorDoc] = useState(null);
  /* Contact Us popup state */
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState(null);
  const CONTACT_MESSAGE_MAX = 180;

  useEffect(() => {
    const loadDocList = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/documentation`);
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success && Array.isArray(data.data)) setDocList(data.data);
      } catch (e) {
        setDocList([]);
      }
    };
    loadDocList();
  }, []);

  const handleDocLinkClick = async (e, docId) => {
    e.preventDefault();
    setShowDocModal(true);
    setLoadingDoc(true);
    setErrorDoc(null);
    setSelectedDoc(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/documentation/${docId}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load document');
      if (data.success && data.data) setSelectedDoc(data.data);
    } catch (err) {
      setErrorDoc(err.message || 'Failed to load document');
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleContactUsClick = (e) => {
    e.preventDefault();
    setShowContactModal(true);
    setContactFirstName('');
    setContactEmail('');
    setContactPhone('');
    setContactMessage('');
    setContactError(null);
  };

  /* Contact form is sent to info@urcommunity.com.au via POST /api/contact (same AWS SES as Setup Billing) */
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const first = (contactFirstName || '').trim();
    const email = (contactEmail || '').trim();
    if (!first || !email) return;
    setContactSubmitting(true);
    setContactError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: first,
          email,
          phone: (contactPhone || '').trim(),
          message: (contactMessage || '').trim()
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setShowContactModal(false);
      } else {
        setContactError(data.error || 'Failed to send. Please try again.');
      }
    } catch (err) {
      setContactError(err.message || 'Failed to send. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <footer className="app-footer" style={{
      background: '#000000',
      borderTop: '1px solid #333333',
      padding: '32px 0 20px 0',
      position: 'relative',
      margin: 0
    }}>
      <div className="container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Top Section */}
        <div className="footer-top" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '24px'
        }}>
          {/* Left Side - Branding and Mission */}
          <div className="footer-branding" style={{
            flex: '1',
            minWidth: '300px',
            maxWidth: '400px'
          }}>
            {/* Logo */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: '#2563eb',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: 'bold'
              }}>
                CF
              </div>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#ffffff'
              }}>
                Chatbot Factory
              </h2>
            </div>
            
            {/* Mission Statement */}
            <p style={{
              color: '#9ca3af',
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '12px',
              margin: 0
            }}>
              An intelligent platform for creating and managing AI-powered chatbots with advanced knowledge base integration and seamless agent configuration.
            </p>
            
            {/* Copyright */}
            <p style={{
              color: '#6b7280',
              fontSize: '12px',
              margin: 0
            }}>
              © 2025 Chatbot Factory. All rights reserved.
            </p>
          </div>
          
          {/* Right Side - Navigation Links in 4 Columns */}
          <div className="footer-navigation" style={{
            display: 'flex',
            gap: '40px',
            flexWrap: 'wrap',
            flex: '2',
            justifyContent: 'flex-end'
          }}>
            {/* Help Center Column (formerly Resources) */}
            <div style={{ minWidth: '120px' }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '12px',
                marginTop: 0
              }}>
                Help Center
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <li>
                  <a
                    href="/documentation"
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Documentation
                  </a>
                </li>
                {/* Demo - COMMENTED OUT */}
                {/* <li>
                  <a 
                    href="/demo" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Demo
                  </a>
                </li> */}
                {/* Support - commented out
                <li>
                  <a 
                    href="/support" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Support
                  </a>
                </li>
                */}
                <li>
                  <a 
                    href="/contact" 
                    onClick={handleContactUsClick}
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Contact Us
                  </a>
                </li>
                <li>
                  <a 
                    href="/acceptable-use-policy" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Acceptable Use Policy
                  </a>
                </li>
                <li>
                  <a
                    href={URCOMMUNITY_CHATBOT_FACTORY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    About Us
                  </a>
                </li>
              </ul>
            </div>
            
            {/* Learning Column - COMMENTED OUT */}
            {/* <div style={{ minWidth: '120px' }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '12px',
                marginTop: 0
              }}>
                Learning
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <li>
                  <a 
                    href="/framework-video" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    <i className="fas fa-video" style={{ fontSize: '12px' }}></i>
                    Framework Video
                  </a>
                </li>
                <li>
                  <a 
                    href="/user-manual" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    <i className="fas fa-file-alt" style={{ fontSize: '12px' }}></i>
                    User Manual
                  </a>
                </li>
                <li>
                  <a 
                    href="/training-program" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Training Program
                  </a>
                </li>
              </ul>
            </div>
            
            Learning & Community sections - COMMENTED OUT */}
            {/* <div style={{ minWidth: '120px' }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '12px',
                marginTop: 0
              }}>
                Community
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <li>
                  <a 
                    href="/soundtrack" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    <i className="fas fa-music" style={{ fontSize: '12px' }}></i>
                    Soundtrack
                  </a>
                </li>
                <li>
                  <a 
                    href="/challenge" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Challenge
                  </a>
                </li>
                <li>
                  <a 
                    href="/schools-community" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Schools & Community
                  </a>
                </li>
                <li>
                  <a 
                    href="/media-press" 
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Media & Press
                  </a>
                </li>
              </ul>
            </div> */}
            
            {/* Policies Column (formerly Legal & Account) - documents from documentation table; each opens popup with that doc */}
            <div style={{ minWidth: '140px' }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '12px',
                marginTop: 0
              }}>
                Policies
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {/* Exclude Acceptable Use Policy from Policies (it is shown under Help Center) */}
                {docList
                  .filter((doc) => (doc.title || '').trim().toLowerCase() !== 'acceptable use policy')
                  .map((doc) => (
                    <li key={doc.id}>
                      <a
                        href={`#doc-${doc.id}`}
                        onClick={(e) => handleDocLinkClick(e, doc.id)}
                        style={linkStyle}
                        onMouseEnter={(e) => { e.target.style.color = '#667eea'; }}
                        onMouseLeave={(e) => { e.target.style.color = '#9ca3af'; }}
                      >
                        {doc.title || `Document #${doc.id}`}
                      </a>
                    </li>
                  ))}
                {/* Rewards Terms - COMMENTED OUT */}
                {/* <li>
                  <a 
                    href="https://globalvacationreward.com/terms/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#667eea'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    Rewards Terms
                  </a>
                </li> */}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Legal documents popup - shown when a Legal & Account document link is clicked */}
      {showDocModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowDocModal(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#111827', fontSize: '18px' }}>{selectedDoc ? (selectedDoc.title || 'Legal') : 'Legal'}</h3>
              <button
                type="button"
                onClick={() => setShowDocModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 4px'
                }}
              >
                ×
              </button>
            </div>
            {loadingDoc && (
              <p style={{ color: '#374151', textAlign: 'center' }}>Loading...</p>
            )}
            {errorDoc && (
              <p style={{ color: '#f87171', margin: 0 }}>{errorDoc}</p>
            )}
            {!loadingDoc && !errorDoc && selectedDoc && (
              <div style={{ color: '#374151', fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{selectedDoc.content || ''}</div>
            )}
            {!loadingDoc && !errorDoc && !selectedDoc && (
              <p style={{ color: '#374151', margin: 0 }}>No document selected.</p>
            )}
          </div>
        </div>
      )}

      {/* Contact Us popup - form as per design */}
      {showContactModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowContactModal(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              maxWidth: '480px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#111827', fontSize: '18px', fontWeight: '600' }}>Contact Us</h3>
              <button
                type="button"
                onClick={() => setShowContactModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleContactSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#111827', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                  First Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={contactFirstName}
                  onChange={(e) => setContactFirstName(e.target.value)}
                  placeholder="E.g. John"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#111827', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                  Email Address <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="E.g. john@doe.com"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#111827', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="E.g. +1 3004005000"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#111827', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                  Message
                </label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value.slice(0, CONTACT_MESSAGE_MAX))}
                  placeholder="Enter your message..."
                  maxLength={CONTACT_MESSAGE_MAX}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    minHeight: '80px',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                  {contactMessage.length}/{CONTACT_MESSAGE_MAX}
                </div>
              </div>
              {contactError && (
                <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px', marginTop: 0 }}>{contactError}</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <button
                  type="submit"
                  disabled={contactSubmitting}
                  style={{
                    background: '#60a5fa',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: contactSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {contactSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer; 